import {AnyImageOpHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal"
import {
    AnyImageOpResult,
    AnyImageOpResultNode,
    ValueOrNode,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {ImageOpNodeBag} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/image-op-node-bag"
import {
    DEFAULT_MAX_PAGES_PER_BATCH_DIM,
    forEachBatchRegion,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/hal/tiled-region-fetch-hal"
import {createTypedArrayImage, halImageToTypedArrayImage} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/typed-array-image-helpers"
import {getChannelLayoutByCount} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/utils/hal-utils"
import {getImgProcChannelLayout} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/utils/imgproc-utils"
import {ImageProcessingNodes as Nodes} from "@cm/image-processing-nodes"
import {TypedImageData} from "@cm/utils/typed-image-data"
import {TypedArrayImage} from "@cm/hal"
import {Box2, Box2Like} from "@cm/math"
import {TexturesApiService} from "@app/textures/service/textures-api.service"
import {ImageColorSpace} from "@generated"

const REF_COUNT_DEBUG_ID_BAKED_MASK_CACHE = "BakedMaskCache.pending"

/**
 * Explicit replacement for ImageOpNodeBakeAndUpload's implicit two-mode behavior. In HAL/preview,
 * captureViaGraph() is a pure pass-through that keeps the last-evaluated result (and the region
 * that should be uploaded) cached here. In imgproc/final, getDataObjectId() turns that cached
 * result into an uploaded data-object id.
 *
 * Callers are responsible for checking their own persisted dataObjectId first and skipping this
 * class entirely (a plain ImageOpNodeDataObject/ImageOpNodePrepareMask load) whenever it's already
 * valid -- this class only ever needs to run when a mask has actually changed.
 */
export class BakedMaskCache {
    private pending?: {hal: AnyImageOpHal; region: Box2Like}

    constructor(
        private texturesApi: TexturesApiService,
        private organizationLegacyId: number,
    ) {}

    /**
     * Wrap `sourceNode` so that whenever the caller's own graph evaluates it in HAL mode, the
     * result is captured here as a side effect -- no separate evaluation, no extra cost beyond
     * what the caller was already doing to show the live mask in preview.
     *
     * `region` is the sub-rectangle that should actually be uploaded (crop) -- omit it for a
     * caller that never crops (e.g. tiling's border-blend mask), in which case the full captured
     * image is used as-is.
     */
    captureViaGraph(
        imageOpNodeBag: ImageOpNodeBag,
        id: string,
        sourceNode: ValueOrNode<AnyImageOpResult>,
        region?: ValueOrNode<Box2Like>,
    ): AnyImageOpResultNode {
        if (region) {
            return imageOpNodeBag.getLambda(id, {sourceImage: sourceNode, region}, ({sourceImage, region}) => {
                this.capture(sourceImage as AnyImageOpHal, region)
                return sourceImage
            })
        }
        return imageOpNodeBag.getLambda(id, {sourceImage: sourceNode}, ({sourceImage}) => {
            this.capture(sourceImage as AnyImageOpHal, Box2.fromSize(sourceImage.descriptor))
            return sourceImage
        })
    }

    /**
     * Called from imgproc/final graph generation once a caller has determined its persisted
     * dataObjectId is stale or missing. Requires captureViaGraph to have already run with a
     * matching HAL pass -- in practice this is always true, since preview always runs before
     * submit; a caller whose mask might not otherwise be reached during preview (e.g. hidden
     * behind a view-mode toggle) must force that HAL pass itself before calling this.
     */
    async getDataObjectId(): Promise<{dataObjectId: string; region: Box2Like}> {
        if (!this.pending) {
            throw new Error("BakedMaskCache.getDataObjectId: no fresh HAL value captured -- captureViaGraph must run first")
        }
        const dataObjectId = await this.uploadHalImageAsExr(this.pending.hal, this.pending.region)
        return {dataObjectId, region: this.pending.region}
    }

    /**
     * Builds a node that reuses the currently-pending captured mask, without re-running whatever
     * graph produced it. Throws if nothing has been captured yet -- prefer
     * nodeFromPendingOrCaptureViaGraph unless the caller has otherwise guaranteed captureViaGraph
     * already ran.
     */
    nodeFromPending(imageOpNodeBag: ImageOpNodeBag, id: string): AnyImageOpResultNode {
        return imageOpNodeBag.getLambda(id, {}, () => {
            if (!this.pending) {
                throw new Error("BakedMaskCache.nodeFromPending: no fresh HAL value captured -- captureViaGraph must run first")
            }
            return this.pending.hal
        })
    }

    /**
     * Reuses the already-captured mask if one exists, otherwise falls back to captureViaGraph to
     * generate (and capture) a fresh one from sourceNode -- lets callers avoid hand-rolling their
     * own "do I already have a valid result" check around captureViaGraph.
     */
    nodeFromPendingOrCaptureViaGraph(
        imageOpNodeBag: ImageOpNodeBag,
        id: string,
        sourceNode: ValueOrNode<AnyImageOpResult>,
        region?: ValueOrNode<Box2Like>,
    ): AnyImageOpResultNode {
        if (this.pending) {
            // distinct id from the captureViaGraph path so a node's shape at a given id stays
            // stable across passes, regardless of which branch ends up running
            return this.nodeFromPending(imageOpNodeBag, id + "FromPending")
        }
        return this.captureViaGraph(imageOpNodeBag, id, sourceNode, region)
    }

    dispose(): void {
        this.pending?.hal.release(REF_COUNT_DEBUG_ID_BAKED_MASK_CACHE)
        this.pending = undefined
    }

    private capture(hal: AnyImageOpHal, region: Box2Like) {
        hal.addRef(REF_COUNT_DEBUG_ID_BAKED_MASK_CACHE)
        this.pending?.hal.release(REF_COUNT_DEBUG_ID_BAKED_MASK_CACHE)
        this.pending = {hal, region}
    }

    private async uploadHalImageAsExr(halImage: AnyImageOpHal, region: Box2Like): Promise<string> {
        const typedArrayImage = createTypedArrayImage({
            width: region.width,
            height: region.height,
            channelLayout: getChannelLayoutByCount(halImage.descriptor.channels),
        })
        // read in shards to avoid one huge simultaneous GPU->CPU readback/allocation, same as
        // ImageOpNodeBakeAndUpload.toImgProc did
        await forEachBatchRegion(halImage.data, region, DEFAULT_MAX_PAGES_PER_BATCH_DIM, (batchHalImage, batchRegion) => {
            halImageToTypedArrayImage({
                source: batchHalImage,
                sourceRegion: batchRegion,
                sourceMipLevel: 0,
                target: typedArrayImage,
                targetOffset: {x: batchRegion.x - region.x, y: batchRegion.y - region.y},
            })
        })

        const maskImageData = this.imageDataFromTypedArrayImage(typedArrayImage)
        const channelLayout = getImgProcChannelLayout(typedArrayImage.channelLayout)
        const convertedImage = Nodes.convert(Nodes.input(maskImageData), "float32", channelLayout, false)
        const mediaType = "image/x-exr"
        const encodedImage = Nodes.encode(convertedImage, mediaType)
        const evaledEncodedImage = await this.texturesApi.evalImageProcessingGraph(encodedImage)
        const dataFile = new File([evaledEncodedImage.data as Uint8Array<ArrayBuffer>], "image.exr")
        const {id} = await this.texturesApi.uploadGqlService.createAndUploadDataObject(
            dataFile,
            {
                organizationLegacyId: this.organizationLegacyId,
                mediaType,
                imageColorSpace: evaledEncodedImage.colorSpace === "linear" ? ImageColorSpace.Linear : ImageColorSpace.Srgb,
                width: evaledEncodedImage.width,
                height: evaledEncodedImage.height,
            },
            {
                showUploadToolbar: false,
                processUpload: false,
            },
        )
        if (!id) {
            throw new Error("Upload succeeded but returned no valid data object ID.")
        }
        return id
    }

    private imageDataFromTypedArrayImage(typedArrayImage: TypedArrayImage): TypedImageData {
        if (typedArrayImage.channelLayout !== "R") {
            throw Error("webGlImage layout must be L")
        }
        return {
            data: typedArrayImage.data,
            width: typedArrayImage.width,
            height: typedArrayImage.height,
            channelLayout: "L",
            dataType: "float32",
            colorSpace: typedArrayImage.dataType === "uint8norm_srgb" ? "sRGB" : "linear",
            dpi: undefined,
        }
    }
}
